import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { Eye, Layers, Search, Waypoints } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Container, CountryFlagIcon } from '../components/ui'
import LiveNetwork from '../components/ui/LiveNetwork'
import { useCurrentUserId } from '../contexts/AuthContext'
import { useNotifications } from '../contexts/NotificationContext'
import { useWatchlist } from '../contexts/WatchlistContext'
import { usePageTitle } from '../hooks/usePageTitle'
import { useRecentSearches } from '../hooks/useRecentSearches'
import { getRecentVisits, type RecentVisit } from '../services/apiClient'
import { fetchNotifications, isUnread } from '../services/notifications'
import type { AppNotification } from '../types/notifications'
import { notificationHref } from '../utils/notificationLinks'
import { visitedLabel } from '../utils/visitLabels'

/**
 * Landing surface — brand copy + search. CVR (8 digits) → /company/:cvr;
 * free-text → /search for companies and persons. Under the search: the
 * user's recent searches (this browser), then the latest changes on their
 * watchlist and the profiles they opened last, side by side on wide
 * screens; first-time users see what Origin does instead.
 */
const CVR_NUMBER_PATTERN = /^\d{8}$/
/** Latest watchlist changes shown on the home page; the inbox has the rest. */
const HOME_CHANGES = 5
const INBOX = '/account/notifikationer/indbakke'

const isWatchlistChange = (n: AppNotification) =>
  n.notificationType === 'watchlist_entity_change'

/** The list picked in »Ændringer på din watchlist«, remembered per user in this browser. */
const homeListKey = (userId: number) =>
  `xosa-origin:home-changes-list:${userId}`

function readHomeList(userId: number | null): number | null {
  if (userId === null) return null
  try {
    const raw = window.localStorage.getItem(homeListKey(userId))
    return raw && /^\d+$/.test(raw) ? Number(raw) : null
  } catch {
    return null
  }
}

function saveHomeList(userId: number | null, listId: number | null): void {
  if (userId === null) return
  try {
    if (listId === null) window.localStorage.removeItem(homeListKey(userId))
    else window.localStorage.setItem(homeListKey(userId), String(listId))
  } catch {
    // Storage blocked: the choice just lasts for this visit.
  }
}

const INTEL_LAYERS = [
  {
    icon: Layers,
    title: 'Ejerskab',
    body: 'Reelle ejere, holdings og ejerskabsstrukturer — fremhævet og læsbart, så du hurtigt ser, hvem der trækker i trådene bag et selskab.',
  },
  {
    icon: Waypoints,
    title: 'Netværk',
    body: 'Følg forbindelser fra selskab til person og videre til meddeltagere. Se overlap og skjulte lag på tværs af strukturer.',
  },
  {
    icon: Eye,
    title: 'Overvågning',
    body: 'Gem selskaber og personer på watchlist. Få besked, når roller, ejerskab eller stamdata ændrer sig.',
  },
] as const

const HomePage: React.FC = () => {
  const [query, setQuery] = useState('')
  const navigate = useNavigate()
  const { searches, remember, clear } = useRecentSearches()
  // null while loading: the area below stays empty instead of flashing the intro cards
  const [visits, setVisits] = useState<RecentVisit[] | null>(null)
  const { recent } = useNotifications()
  const changes = recent.filter(isWatchlistChange).slice(0, HOME_CHANGES)
  const hasVisits = visits !== null && visits.length > 0
  // Side by side on wide screens: both sections share heading / filter /
  // list rows (subgrid), so the two lists start on the same line.
  const paired = changes.length > 0 && hasVisits
  usePageTitle('Søg')

  useEffect(() => {
    let cancelled = false
    getRecentVisits(6)
      .then(rows => {
        if (!cancelled) setVisits(rows)
      })
      .catch(() => {
        if (!cancelled) setVisits([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const q = query.trim()
    if (!q) return
    remember(q)
    if (CVR_NUMBER_PATTERN.test(q)) {
      navigate(`/company/${q}`)
    } else {
      navigate(`/search?q=${encodeURIComponent(q)}`)
    }
  }

  return (
    <div className="min-h-[calc(100vh-3.5rem)]">
      <Container className="pt-12 pb-24 sm:pt-16">
        <div className="grid items-center gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:gap-12">
          <div className="min-w-0">
            <h1 className="text-[34px] sm:text-[48px] leading-[1.05] text-ink">
              Hold øje med bagmændene.
            </h1>
            <p className="mt-4 text-base sm:text-[17px] text-ink-700 max-w-[52ch] leading-relaxed">
              Se hvem, der står bag virksomheder. Se gennem deres netværk. Få
              notifikationer, når nogen gør noget.
            </p>

            <form onSubmit={handleSubmit} className="mt-8">
              <label htmlFor="home-search" className="sr-only">
                Søg på CVR-nummer, virksomheder eller personer
              </label>
              <div className="flex gap-2">
                <input
                  id="home-search"
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Søg på CVR-nummer, virksomheder eller personer"
                  autoFocus
                  className="min-w-0 flex-1 h-[52px] rounded-lg border border-rule-strong bg-paper px-4 text-base text-ink placeholder:text-ink-400 hover:border-ink-300 transition-colors"
                />
                <button
                  type="submit"
                  disabled={!query.trim()}
                  className="h-[52px] shrink-0 rounded-lg px-4 sm:px-6 bg-ink text-paper text-sm font-semibold hover:bg-ink-700 disabled:bg-ink-300 inline-flex items-center gap-2 transition-colors"
                >
                  <Search className="h-5 w-5" aria-hidden />
                  <span className="hidden sm:inline">Slå op</span>
                </button>
              </div>
            </form>

            {searches.length > 0 && (
              <div
                className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-2 text-sm"
                data-testid="recent-searches"
              >
                <span className="mr-1 text-ink-500">Senest søgt:</span>
                {searches.map(q => (
                  <Link
                    key={q}
                    to={
                      CVR_NUMBER_PATTERN.test(q)
                        ? `/company/${q}`
                        : `/search?q=${encodeURIComponent(q)}`
                    }
                    className="inline-flex h-8 max-w-[16rem] items-center rounded-full border border-rule-strong bg-paper px-3 text-ink hover:border-ink-300 transition-colors"
                  >
                    <span className="truncate">{q}</span>
                  </Link>
                ))}
                <button
                  type="button"
                  onClick={clear}
                  className="ml-1 text-[13px] text-ink-500 underline decoration-ink-300 underline-offset-[3px] hover:text-ink hover:decoration-ink"
                >
                  Ryd
                </button>
              </div>
            )}
          </div>

          <div className="relative h-56 sm:h-72 lg:h-[400px]">
            <LiveNetwork className="absolute inset-0" />
          </div>
        </div>

        {(changes.length > 0 || hasVisits) && (
          <div
            className={`mt-12 grid gap-10 lg:mt-14 ${paired ? 'lg:grid-cols-2 lg:gap-x-8 lg:gap-y-0' : ''}`}
          >
            {changes.length > 0 && (
              <WatchlistChanges changes={changes} paired={paired} />
            )}
            {hasVisits && <RecentlyOpened visits={visits} paired={paired} />}
          </div>
        )}
        {visits && visits.length === 0 && changes.length === 0 && (
          <section
            aria-label="Ejerskab, netværk og overvågning"
            className="mt-12 lg:mt-14"
          >
            <ul className="grid gap-3 sm:grid-cols-3 sm:gap-4">
              {INTEL_LAYERS.map(({ icon, title, body }) => (
                <IntelLayerCard
                  key={title}
                  icon={icon}
                  title={title}
                  body={body}
                />
              ))}
            </ul>
          </section>
        )}
      </Container>
    </div>
  )
}

/** Company square / person pill, the same shapes as in the ownership graph. */
const EntityGlyph: React.FC<{ type: RecentVisit['entityType'] }> = ({
  type,
}) => (
  <span
    aria-hidden
    className={`mt-[5px] shrink-0 border-[1.5px] border-ink-400 ${
      type === 'company' ? 'h-3 w-3 rounded-[3px]' : 'h-3 w-[18px] rounded-full'
    }`}
  />
)

const SectionHeading: React.FC<{
  id: string
  children: React.ReactNode
  aside?: React.ReactNode
}> = ({ id, children, aside }) => (
  <div className="flex items-baseline justify-between gap-4">
    <h2 id={id} className="text-[17px] font-bold text-ink">
      {children}
    </h2>
    {aside}
  </div>
)

/**
 * The newest watchlist notifications, unread ones marked with the signal
 * dot like in the bell. Opening one marks it read. Users with lists can
 * narrow it to one list; the choice is remembered in this browser.
 */
const PAIRED_SECTION = 'lg:row-span-3 lg:grid lg:grid-rows-subgrid'

const WatchlistChanges: React.FC<{
  changes: AppNotification[]
  paired?: boolean
}> = ({ changes: all, paired = false }) => {
  const { markRead } = useNotifications()
  const { lists } = useWatchlist()
  const userId = useCurrentUserId()
  const [listId, setListId] = useState<number | null>(() =>
    readHomeList(userId)
  )
  const [filtered, setFiltered] = useState<{
    listId: number
    items: AppNotification[]
  } | null>(null)
  // A remembered list that has since been deleted falls back to »Alle«.
  const active =
    listId !== null && lists.some(l => l.id === listId) ? listId : null

  useEffect(() => {
    if (active === null) return
    let cancelled = false
    fetchNotifications(HOME_CHANGES * 2, 0, false, { listId: active })
      .then(res => {
        if (!cancelled)
          setFiltered({
            listId: active,
            items: res.items.filter(isWatchlistChange).slice(0, HOME_CHANGES),
          })
      })
      .catch(() => {
        if (!cancelled) setFiltered({ listId: active, items: [] })
      })
    return () => {
      cancelled = true
    }
  }, [active])

  const pending = active !== null && filtered?.listId !== active
  // While another list loads, the previous rows stay (dimmed) instead of jumping.
  const changes = active === null ? all : (filtered?.items ?? all)
  const unread = changes.filter(isUnread).length
  const activeName = lists.find(l => l.id === active)?.name

  function choose(id: number | null) {
    setListId(id)
    saveHomeList(userId, id)
  }

  function open(n: AppNotification) {
    if (!isUnread(n)) return
    void markRead(n.id).catch(() => undefined)
    setFiltered(
      prev =>
        prev && {
          ...prev,
          items: prev.items.map(x =>
            x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x
          ),
        }
    )
  }

  return (
    <section
      aria-labelledby="watchlist-changes-h"
      className={`min-w-0 ${paired ? PAIRED_SECTION : ''}`}
      data-testid="watchlist-changes"
    >
      <SectionHeading
        id="watchlist-changes-h"
        aside={
          <Link
            to={active === null ? INBOX : `${INBOX}?liste=${active}`}
            className="shrink-0 text-[13px] font-semibold text-ink-700 underline decoration-ink-300 underline-offset-[3px] hover:text-ink hover:decoration-ink"
          >
            {unread > 0
              ? `${unread} ${unread === 1 ? 'ny' : 'nye'} · Se alle`
              : 'Se alle'}
          </Link>
        }
      >
        Ændringer på din watchlist
      </SectionHeading>
      {lists.length > 0 ? (
        <div
          role="group"
          aria-label="Vis ændringer fra"
          className="-mx-1 mt-2 flex gap-1.5 overflow-x-auto px-1 pb-1"
        >
          {[{ id: null, name: 'Alle' }, ...lists].map(l => {
            const on = l.id === active
            return (
              <button
                key={l.id ?? 'all'}
                type="button"
                aria-pressed={on}
                onClick={() => choose(l.id)}
                className={`h-7 max-w-[14rem] shrink-0 truncate rounded-full border px-3 text-[13px] font-semibold transition-colors ${
                  on
                    ? 'border-ink bg-ink text-paper'
                    : 'border-rule-strong bg-paper text-ink-700 hover:border-ink-300 hover:text-ink'
                }`}
              >
                {l.name}
              </button>
            )
          })}
        </div>
      ) : (
        // Keeps the shared filter row when paired with »Senest åbnet«
        <div aria-hidden />
      )}
      {changes.length === 0 && !pending ? (
        <p className="mt-3 self-start rounded-[10px] border border-rule bg-paper px-4 py-6 text-sm text-ink-500">
          Ingen ændringer på »{activeName}« endnu.
        </p>
      ) : (
        <ul
          aria-busy={pending}
          className={`mt-3 self-start overflow-hidden rounded-[10px] border border-rule bg-paper transition-opacity ${pending ? 'opacity-60' : ''}`}
        >
          {changes.map(n => {
            const fresh = isUnread(n)
            const href = notificationHref(n) ?? INBOX
            const country =
              typeof n.payload?.countryCode === 'string'
                ? n.payload.countryCode
                : null
            return (
              <li key={n.id} className="-mt-px border-t border-rule-soft">
                <Link
                  to={href}
                  onClick={() => open(n)}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-canvas transition-colors"
                >
                  <span
                    className="flex h-[22px] w-2 shrink-0 items-center justify-center"
                    aria-hidden
                  >
                    {fresh && (
                      <span className="block h-1.5 w-1.5 rounded-full bg-signal" />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="sr-only">{fresh ? 'Ny. ' : ''}</span>
                    <span
                      className={`block truncate text-[15px] text-ink ${fresh ? 'font-bold' : 'font-semibold'}`}
                    >
                      {n.title}
                    </span>
                    <span className="mt-0.5 block text-[13.5px] text-ink-500 line-clamp-2">
                      {country && (
                        <CountryFlagIcon
                          code={country}
                          focusable={false}
                          className="mr-1.5"
                        />
                      )}
                      {n.summary}
                    </span>
                  </span>
                  <span className="shrink-0 pt-0.5 text-[13px] text-ink-500 tabular-nums">
                    {visitedLabel(n.createdAt)}
                  </span>
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

const RecentlyOpened: React.FC<{ visits: RecentVisit[]; paired?: boolean }> = ({
  visits,
  paired = false,
}) => (
  <section
    aria-labelledby="recent-visits-h"
    className={`min-w-0 ${paired ? PAIRED_SECTION : ''}`}
    data-testid="recent-visits"
  >
    <SectionHeading id="recent-visits-h">Senest åbnet</SectionHeading>
    {paired && <div aria-hidden />}
    <ul
      className={`mt-3 grid self-start overflow-hidden rounded-[10px] border border-rule bg-paper ${paired ? '' : 'sm:grid-cols-2'}`}
    >
      {visits.map(v => {
        const href =
          v.entityType === 'company'
            ? `/company/${v.entityId}`
            : `/person/${v.entityId}`
        const name =
          v.name ||
          (v.entityType === 'company'
            ? `CVR ${v.entityId}`
            : `Person ${v.entityId}`)
        return (
          <li
            key={`${v.entityType}:${v.entityId}`}
            className={`-mt-px border-t border-rule-soft ${
              paired
                ? ''
                : 'sm:odd:border-r sm:[&:last-child:nth-child(odd)]:col-span-2 sm:[&:last-child:nth-child(odd)]:border-r-0'
            }`}
          >
            <Link
              to={href}
              className="flex items-start gap-3 px-4 py-3 hover:bg-canvas transition-colors"
            >
              <EntityGlyph type={v.entityType} />
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2 text-[15px] font-bold text-ink">
                  <span className="truncate">{name}</span>
                  {v.countryCode && v.countryCode !== 'DK' && (
                    <CountryFlagIcon code={v.countryCode} />
                  )}
                </span>
                {v.detail && (
                  <span className="mt-0.5 block truncate text-[13.5px] text-ink-500">
                    {v.detail}
                  </span>
                )}
              </span>
              <span className="shrink-0 pt-0.5 text-[13px] text-ink-500 tabular-nums">
                {visitedLabel(v.visitedAt)}
              </span>
            </Link>
          </li>
        )
      })}
    </ul>
  </section>
)

interface IntelLayerCardProps {
  icon: LucideIcon
  title: string
  body: string
}

const IntelLayerCard: React.FC<IntelLayerCardProps> = ({
  icon: Icon,
  title,
  body,
}) => {
  return (
    <li className="border border-rule rounded-[10px] p-5 bg-paper">
      <div className="flex items-center gap-3 mb-2">
        <Icon
          className="h-6 w-6 shrink-0 text-ink-500"
          strokeWidth={1.25}
          aria-hidden
        />
        <h3 className="text-base font-bold text-ink">{title}</h3>
      </div>
      <p className="text-sm text-ink-700 leading-relaxed">{body}</p>
    </li>
  )
}

export default HomePage
